import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  MessageSquare, Search, Clock, User, Mail, 
  CheckCircle, AlertCircle, XCircle, RefreshCw,
  ChevronRight, ArrowLeft, Filter, Send
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: AlertCircle },
  in_progress: { label: 'In Progress', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
  resolved: { label: 'Resolved', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: XCircle }
};

const AdminSupportTicketsPage = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const adminKey = sessionStorage.getItem('aura_admin_key');
      const response = await axios.get(`${API}/support/tickets`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const updateTicketStatus = async (ticketId, newStatus) => {
    try {
      const adminKey = sessionStorage.getItem('aura_admin_key');
      await axios.put(`${API}/support/tickets/${ticketId}/status`, 
        { status: newStatus },
        { headers: { 'X-Admin-Key': adminKey } }
      );
      
      setTickets(prev => prev.map(t => 
        t.ticket_id === ticketId ? { ...t, status: newStatus } : t
      ));
      
      if (selectedTicket?.ticket_id === ticketId) {
        setSelectedTicket(prev => ({ ...prev, status: newStatus }));
      }
      
      toast.success(`Ticket marked as ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
    } catch (error) {
      toast.error('Failed to update ticket status');
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    
    setSending(true);
    try {
      const adminKey = sessionStorage.getItem('aura_admin_key');
      await axios.post(`${API}/support/tickets/${selectedTicket.ticket_id}/reply`, 
        { message: replyText },
        { headers: { 'X-Admin-Key': adminKey } }
      );
      
      // Add reply to local state
      const newReply = {
        message: replyText,
        from: 'admin',
        admin_name: user?.name || 'Support Team',
        created_at: new Date().toISOString()
      };
      
      setSelectedTicket(prev => ({
        ...prev,
        replies: [...(prev.replies || []), newReply]
      }));
      
      // Update ticket in list
      setTickets(prev => prev.map(t => 
        t.ticket_id === selectedTicket.ticket_id 
          ? { ...t, replies: [...(t.replies || []), newReply], status: 'in_progress' }
          : t
      ));
      
      setReplyText('');
      toast.success('Reply sent successfully');
      
      // Auto-update status to in_progress if it was new
      if (selectedTicket.status === 'new') {
        updateTicketStatus(selectedTicket.ticket_id, 'in_progress');
      }
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = searchQuery === '' || 
      ticket.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.user_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: tickets.length,
    new: tickets.filter(t => t.status === 'new').length,
    inProgress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-aura-bg flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-aura-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-aura-bg">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-aura-bg/95 backdrop-blur-lg border-b border-white/5">
        <div className="flex items-center justify-between h-14 px-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="p-2 text-aura-text-muted hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="w-8 h-8 bg-purple-500 rounded-sm flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-lg text-white tracking-tight">SUPPORT TICKETS</span>
          </div>
          <Button onClick={fetchTickets} variant="ghost" className="text-aura-text-muted">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="px-4 py-6 max-w-7xl mx-auto space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-bold text-white">{stats.total}</p>
              <p className="text-xs text-aura-text-muted uppercase">Total</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-bold text-blue-400">{stats.new}</p>
              <p className="text-xs text-blue-400 uppercase">New</p>
            </CardContent>
          </Card>
          <Card className="bg-yellow-500/10 border-yellow-500/30">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-bold text-yellow-400">{stats.inProgress}</p>
              <p className="text-xs text-yellow-400 uppercase">In Progress</p>
            </CardContent>
          </Card>
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-bold text-green-400">{stats.resolved}</p>
              <p className="text-xs text-green-400 uppercase">Resolved</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-aura-text-muted" />
            <Input
              placeholder="Search by subject, email, or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-aura-surface border-white/10 text-white"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-aura-surface border-white/10 text-white">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent className="bg-aura-surface border-white/10">
              <SelectItem value="all" className="text-white">All Tickets</SelectItem>
              <SelectItem value="new" className="text-white">New</SelectItem>
              <SelectItem value="in_progress" className="text-white">In Progress</SelectItem>
              <SelectItem value="resolved" className="text-white">Resolved</SelectItem>
              <SelectItem value="closed" className="text-white">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tickets List */}
        <Card className="bg-aura-surface border-white/10">
          <CardContent className="p-0">
            {filteredTickets.length === 0 ? (
              <div className="p-8 text-center">
                <MessageSquare className="w-12 h-12 text-aura-text-muted mx-auto mb-4" />
                <p className="text-white font-medium">No tickets found</p>
                <p className="text-aura-text-muted text-sm mt-1">
                  {searchQuery || statusFilter !== 'all' 
                    ? 'Try adjusting your filters'
                    : 'Support tickets will appear here'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {filteredTickets.map((ticket) => {
                  const StatusIcon = STATUS_CONFIG[ticket.status]?.icon || AlertCircle;
                  const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.new;
                  
                  return (
                    <div
                      key={ticket.ticket_id}
                      onClick={() => setSelectedTicket(ticket)}
                      className="p-4 hover:bg-white/5 cursor-pointer transition-colors"
                      data-testid={`ticket-${ticket.ticket_id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={`${statusConfig.color} border text-xs`}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                            {ticket.replies?.length > 0 && (
                              <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">
                                {ticket.replies.length} replies
                              </Badge>
                            )}
                          </div>
                          <p className="font-medium text-white truncate">{ticket.subject}</p>
                          <p className="text-sm text-aura-text-muted truncate mt-1">{ticket.message}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-aura-text-muted">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {ticket.user_name || 'Anonymous'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {ticket.user_email || 'No email'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {ticket.created_at 
                                ? formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })
                                : 'Unknown'}
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-aura-text-muted flex-shrink-0" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ticket Detail Dialog */}
        <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
          <DialogContent className="bg-aura-surface border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
            {selectedTicket && (
              <>
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <DialogTitle className="font-heading font-bold text-white uppercase tracking-tight">
                      Ticket Details
                    </DialogTitle>
                    <Select 
                      value={selectedTicket.status} 
                      onValueChange={(v) => updateTicketStatus(selectedTicket.ticket_id, v)}
                    >
                      <SelectTrigger className={`w-36 ${STATUS_CONFIG[selectedTicket.status]?.color || ''} border`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-aura-surface border-white/10">
                        <SelectItem value="new" className="text-white">New</SelectItem>
                        <SelectItem value="in_progress" className="text-white">In Progress</SelectItem>
                        <SelectItem value="resolved" className="text-white">Resolved</SelectItem>
                        <SelectItem value="closed" className="text-white">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </DialogHeader>

                <div className="space-y-4">
                  {/* Ticket Info */}
                  <div className="bg-white/5 rounded-lg p-4 space-y-2">
                    <p className="font-medium text-white text-lg">{selectedTicket.subject}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-aura-text-muted">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {selectedTicket.user_name || 'Anonymous'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {selectedTicket.user_email || 'No email'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {selectedTicket.created_at 
                          ? format(new Date(selectedTicket.created_at), 'MMM d, yyyy h:mm a')
                          : 'Unknown'}
                      </span>
                    </div>
                  </div>

                  {/* Original Message */}
                  <div className="space-y-2">
                    <p className="text-xs text-aura-text-muted uppercase">Original Message</p>
                    <div className="bg-aura-bg rounded-lg p-4">
                      <p className="text-white whitespace-pre-wrap">{selectedTicket.message}</p>
                    </div>
                  </div>

                  {/* Replies */}
                  {selectedTicket.replies?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs text-aura-text-muted uppercase">Conversation</p>
                      <div className="space-y-3">
                        {selectedTicket.replies.map((reply, idx) => (
                          <div 
                            key={idx} 
                            className={`rounded-lg p-3 ${
                              reply.from === 'admin' 
                                ? 'bg-purple-500/10 border border-purple-500/30 ml-4' 
                                : 'bg-white/5 mr-4'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-aura-text-muted">
                                {reply.from === 'admin' ? (reply.admin_name || 'Support Team') : selectedTicket.user_name}
                              </span>
                              <span className="text-xs text-aura-text-muted">
                                {reply.created_at 
                                  ? formatDistanceToNow(new Date(reply.created_at), { addSuffix: true })
                                  : ''}
                              </span>
                            </div>
                            <p className="text-white text-sm whitespace-pre-wrap">{reply.message}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Reply Form */}
                  {selectedTicket.status !== 'closed' && (
                    <div className="space-y-2 pt-4 border-t border-white/10">
                      <p className="text-xs text-aura-text-muted uppercase">Send Reply</p>
                      <Textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Type your reply..."
                        rows={3}
                        className="bg-aura-bg border-white/10 text-white"
                      />
                      <div className="flex gap-2">
                        <Button
                          onClick={sendReply}
                          disabled={!replyText.trim() || sending}
                          className="bg-purple-500 hover:bg-purple-600 text-white"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          {sending ? 'Sending...' : 'Send Reply'}
                        </Button>
                        <Button
                          onClick={() => updateTicketStatus(selectedTicket.ticket_id, 'resolved')}
                          variant="outline"
                          className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Mark Resolved
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminSupportTicketsPage;
